const lootsApi = require('../api/loots');

const { addPurchase, addPurchases } = require('./PurchaseActions');
const {
  updateLootcoinBalance,
  updateLootgemBalance,
} = require('./UserActions');

const MIN_LOOTS_UPDATE = 1000 * 60 * 5; // 5 minutes
const FETCH_LOOTS_TIMEOUT = 1000 * 30; // 30 seconds

function fetchLootInProgress(lootId) {
  return {
    type: 'FETCH_LOOT_IN_PROGRESS',
    lootId,
    timestamp: Date.now(),
  };
}

function fetchLootSuccessful(loot) {
  return {
    type: 'FETCH_LOOT_SUCCESSFUL',
    loot,
    timestamp: Date.now(),
  };
}

function fetchLootFailed(lootId, reason) {
  return {
    type: 'FETCH_LOOT_FAILED',
    lootId,
    reason,
    timestamp: Date.now(),
  };
}

function fetchLoot(lootId, { country, platform } = {}) {
  return (dispatch, getState) => {
    dispatch(fetchLootInProgress(lootId));

    const state = getState();

    return lootsApi
      .fetchLoot(state.auth.apiUrl, lootId, { country, platform })
      .then(
        (loot) => {
          dispatch(fetchLootSuccessful(loot));
        },
        (error) => {
          dispatch(fetchLootFailed(lootId, error));
          throw error;
        },
      );
  };
}

function shouldFetchLoot(loot, skipOutdatedCheck) {
  const { fetchingInProgressSince, didInvalidate, lastSuccessfulFetch } =
    loot || {};

  if (!fetchingInProgressSince) {
    // Should update if it was explicetely invalidated
    if (didInvalidate) {
      return true;
    }

    // Should update if data is too old
    if (
      !skipOutdatedCheck &&
      (!lastSuccessfulFetch ||
        Date.now() - lastSuccessfulFetch > MIN_LOOTS_UPDATE)
    ) {
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_LOOTS_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

function fetchLootIfNeeded(lootId, { country, platform } = {}) {
  return (dispatch, getState) => {
    const loots = getState().loots.items;
    const loot =
      loots[lootId] && loots[lootId].lastSuccessfulFetch
        ? loots[lootId]
        : Object.keys(loots)
            .map((key) => loots[key])
            .find((lootEntry) => lootEntry.urlAlias === lootId);
    if (shouldFetchLoot(loot)) {
      return dispatch(fetchLoot(lootId, { country, platform }));
    }
    return Promise.resolve();
  };
}

function invalidateLoots() {
  return {
    type: 'INVALIDATE_LOOTS',
    timestamp: Date.now(),
  };
}

function createPurchase(lootId, paymentDetails) {
  return (dispatch, getState) => {
    const state = getState();
    const { token } = state.auth;
    const { apiUrl } = state.auth;

    return lootsApi
      .createPurchase(apiUrl, token, lootId, paymentDetails)
      .then((purchase) => {
        if (purchase && purchase.status === 'PAID') {
          const { purchases, ...purchaseToAdd } = purchase;
          dispatch(updateLootcoinBalance(purchase.newLootcoinBalance));
          if (typeof purchase.newLootgemBalance === 'number') {
            dispatch(updateLootgemBalance(purchase));
          }
          dispatch(addPurchase(purchaseToAdd));
          if (Array.isArray(purchases)) {
            purchases
              .filter((p) => p.status === 'PAID')
              .forEach((p) => dispatch(addPurchase(p)));
          }
        }
        return purchase;
      });
  };
}

function createBundlePurchase(lootId, paymentDetails, bundle) {
  return (dispatch, getState) => {
    const state = getState();
    const { token } = state.auth;
    const { apiUrl } = state.auth;

    return lootsApi
      .createBundlePurchase(apiUrl, token, lootId, {
        ...(paymentDetails || {}),
        bundle,
      })
      .then((bundlePurchase) => {
        const { purchases, newLootcoinBalance, newLootgemBalance } =
          bundlePurchase || {};

        const [purchase] = purchases || [];
        if (purchase && purchase.status === 'PAID') {
          dispatch(updateLootcoinBalance(newLootcoinBalance));
          if (typeof newLootgemBalance === 'number') {
            dispatch(updateLootgemBalance({ newLootgemBalance }));
          }
          dispatch(
            addPurchases((purchases || []).filter((p) => p.status === 'PAID')),
          );
        }
        return purchases;
      });
  };
}

function confirmPurchase(lootId, transactionId, paymentDetails) {
  return (dispatch, getState) => {
    const state = getState();
    const { token } = state.auth;
    const { apiUrl } = state.auth;

    return lootsApi.confirmPurchase(
      apiUrl,
      token,
      lootId,
      transactionId,
      paymentDetails,
    );
  };
}

function createSubscription(lootId, paymentDetails) {
  return (dispatch, getState) => {
    const state = getState();
    const { token } = state.auth;
    const { apiUrl } = state.auth;

    return lootsApi
      .createSubscription(apiUrl, token, lootId, paymentDetails)
      .then((subscription) => {
        if (
          subscription &&
          subscription.status === 'ACTIVE' &&
          subscription.purchase &&
          subscription.purchase.status === 'PAID'
        ) {
          dispatch(addPurchase(subscription.purchase));
        }
        return subscription;
      });
  };
}

function confirmSubscription(lootId, transactionId, paymentDetails) {
  return (dispatch, getState) => {
    const state = getState();
    const { token } = state.auth;
    const { apiUrl } = state.auth;

    return lootsApi.confirmSubscription(
      apiUrl,
      token,
      lootId,
      transactionId,
      paymentDetails,
    );
  };
}

module.exports = {
  fetchLootIfNeeded,
  fetchLoot,
  createPurchase,
  createBundlePurchase,
  confirmPurchase,
  createSubscription,
  confirmSubscription,
  invalidateLoots,
};
